<script setup lang="ts">
/**
 * Текст в ячейке таблицы: максимум 2 строки, остальное под «Развернуть» / «Свернуть».
 * Стиль кнопки как у CollapsibleCell в аудитах (text-primary + ri-arrow-*-s-fill).
 */

const props = defineProps<{
  text?: string | null
}>()

const isExpanded = ref(false)
const textRef = ref<HTMLElement | null>(null)
const canToggle = ref(false)

function measureOverflow() {
  if (isExpanded.value)
    return

  const el = textRef.value
  if (!el)
    return

  /* Не сбрасываем canToggle: появление кнопки меняет высоту ячейки и ломает замер */
  if (el.scrollHeight > el.clientHeight + 1)
    canToggle.value = true
}

watch(
  () => props.text,
  async () => {
    isExpanded.value = false
    canToggle.value = false
    await nextTick()
    measureOverflow()
  },
)

onMounted(async () => {
  await nextTick()
  measureOverflow()
})

useResizeObserver(textRef, () => {
  measureOverflow()
})

function toggle() {
  isExpanded.value = !isExpanded.value
  if (!isExpanded.value)
    nextTick(() => measureOverflow())
}
</script>

<template>
  <div
    v-if="text"
    class="collapsible-text-root d-flex flex-column align-start ga-1"
  >
    <div
      ref="textRef"
      class="collapsible-text"
      :class="{ 'collapsible-text--clamped': !isExpanded }"
    >
      {{ text }}
    </div>

    <span
      v-if="canToggle || isExpanded"
      class="text-primary cursor-pointer d-inline-flex align-center"
      @click.stop="toggle"
    >
      {{ isExpanded ? 'Свернуть' : 'Развернуть' }}
      <VIcon
        :icon="isExpanded ? 'ri-arrow-up-s-fill' : 'ri-arrow-down-s-fill'"
        size="14"
        :class="{ 'collapsible-text__arrow-up': isExpanded }"
      />
    </span>
  </div>
</template>

<style scoped lang="scss">
.collapsible-text-root {
  min-inline-size: 0;
  max-inline-size: 100%;
  inline-size: 100%;
}

.collapsible-text {
  min-inline-size: 0;
  max-inline-size: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.collapsible-text--clamped {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

/* Глиф ri-arrow-up-s-fill смещён к верху — как в CollapsibleCell */
.collapsible-text__arrow-up {
  transform: translateY(2px);
}
</style>
